import React from 'react'

import { useMessaging } from '@/shared/components/Chat/context/Messaging.context'
import { Logout } from '@mui/icons-material'
import { Box, Collapse, Divider, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import { CheckCircle, ChevronDown, LogOut, User } from 'lucide-react'

import Avatar from '../../../Avatar/Avatar.component'
import ResponsiveDisplayWrapper from '../../../ResponsiveDisplayWrapper/ResponsiveDisplayWrapper.component'
import AuthButtons from '../AuthButtons/AuthButtons.component'
import MessagingNotification from '../MessagingNotification/MessagingNotification.component'

import { logout } from '../../../../../redux/states/auth/logout/logout.slice'
import router from '../../../../../router/functions/router.functions'
import routes from '../../../../../router/routes.dictionary'
import logger from '../../../../functions/Logger/logger.functions'
import { UserRole } from '../../../../functions/UserRole/userRoleManagement.types'
import { getUserRole, setUserRole } from '../../../../functions/UserRole/userRoleManagment.functions'
import useMenu from '../../../../hooks/menu.hooks'
import { useAppDispatch } from '../../../../hooks/redux.hooks'
import styles from './ProfileMenu.styles'
import { ProfileMenuProps } from './ProfileMenu.types'

const roleOptions = [
  { value: UserRole.sender, label: 'Sender', route: routes.send.path },
  { value: UserRole.traveler, label: 'Traveler', route: routes.home.path },
  { value: UserRole.shopper, label: 'Shopper', route: routes.shop.path }
]

const ProfileMenu: React.FC<ProfileMenuProps> = (props) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [roleSectionOpen, setRoleSectionOpen] = React.useState(false)
  const [selectedRole, setSelectedRole] = React.useState<UserRole>((getUserRole() as UserRole) || UserRole.traveler)
  const isProfileMenuOpen = Boolean(anchorEl)

  const navigate = router.navigate()
  const dispatch = useAppDispatch()
  const messaging = useMessaging()
  const [getAdjustedMarginTop] = useMenu()

  const handleClickProfileMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    logger.logEvent(`profile_menu_opened_${router.getCurrentPage().path}`)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setRoleSectionOpen(false)
  }

  const handleLogout = () => {
    const onSuccess = () => {
      navigate(routes.home.path)
    }

    const onError = () => {
      navigate(routes.login.path)
    }

    dispatch(logout({ onSuccess, onError }))

    messaging.closeChat()
  }

  const handleRoleChange = (newRole: UserRole) => {
    setUserRole(newRole)
    setSelectedRole(newRole)
    const roleOption = roleOptions.find((option) => option.value === newRole)

    if (roleOption) {
      navigate(roleOption.route)
    }

    handleClose()
  }

  const currentRoleLabel = roleOptions.find((option) => option.value === selectedRole)?.label || 'Traveler'

  const menuItems = [
    {
      label: 'Profile',
      onClick: () => {
        handleClose()
        navigate(routes.profile.path)
        logger.logEvent(`profile_option_clicked_${router.getCurrentPage().path}`)
      }
    },
    {
      label: 'Logout',
      onClick: () => {
        handleClose()
        handleLogout()
        logger.logEvent(`logout_option_clicked_${router.getCurrentPage().path}`)
      }
    }
  ]

  const getMenuList = () => {
    if (props.isAuthenticated) {
      return (
        <>
          {/* Desktop — unchanged */}
          <ResponsiveDisplayWrapper isDesktop={true}>
            {menuItems.map((item, index) => {
              if (item.label !== 'Logout') {
                return (
                  <MenuItem key={index} onClick={item.onClick}>
                    {item.label}
                  </MenuItem>
                )
              } else {
                return (
                  <Box key={index}>
                    <Divider />
                    <MenuItem key={item.label} onClick={item.onClick}>
                      <ListItemIcon>
                        <Logout fontSize="small" />
                      </ListItemIcon>
                      {item.label}
                    </MenuItem>
                  </Box>
                )
              }
            })}
          </ResponsiveDisplayWrapper>

          {/* Mobile */}
          <ResponsiveDisplayWrapper isDesktop={false} fullWidth>
            {/* Role Switcher Section */}
            <Box sx={styles.roleSwitcherSection}>
              <Box
                sx={styles.roleSwitcherHeader}
                onClick={(e) => {
                  e.stopPropagation()
                  setRoleSectionOpen(!roleSectionOpen)
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  <Typography sx={styles.roleLabelText}>USE AS A</Typography>
                  <Typography sx={styles.currentRoleText}>{currentRoleLabel}</Typography>
                </Box>
                <Box sx={styles.chevron(roleSectionOpen)}>
                  <ChevronDown size={16} color="#9CA3AF" />
                </Box>
              </Box>

              <Collapse in={roleSectionOpen}>
                {roleOptions.map((option) => {
                  const isActive = option.value === selectedRole

                  return (
                    <Box
                      key={option.value}
                      sx={styles.roleOption(isActive)}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRoleChange(option.value)
                      }}
                    >
                      {isActive && <CheckCircle size={18} color="#4F46E5" />}
                      <Typography sx={styles.roleOptionText(isActive)}>{option.label}</Typography>
                    </Box>
                  )
                })}
              </Collapse>
            </Box>

            {/* Divider */}
            <Box sx={styles.divider} />

            {/* Profile */}
            <Box sx={styles.navItem} onClick={menuItems[0].onClick}>
              <User size={18} color="#374151" />
              <Typography sx={styles.navItemText}>Profile</Typography>
            </Box>

            {/* Logout */}
            <Box sx={styles.navItem} onClick={menuItems[1].onClick}>
              <LogOut size={18} color="#EF4444" />
              <Typography sx={{ ...styles.navItemText, color: '#EF4444' }}>Logout</Typography>
            </Box>
          </ResponsiveDisplayWrapper>
        </>
      )
    }

    return (
      <ResponsiveDisplayWrapper isDesktop={false}>
        <AuthButtons currentPage={router.getCurrentPage()} />
      </ResponsiveDisplayWrapper>
    )
  }

  return (
    <Box sx={props.containerStyle}>
      <Box sx={styles.rightSideArea}>
        <MessagingNotification />

        <Tooltip title="Account Settings">
          <IconButton
            onClick={handleClickProfileMenu}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={isProfileMenuOpen ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isProfileMenuOpen ? 'true' : undefined}
          >
            <Avatar size="small" />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={isProfileMenuOpen}
          onClose={handleClose}
          slotProps={{
            paper: styles.paper
          }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          sx={styles.profileMenu(getAdjustedMarginTop())}
        >
          {getMenuList()}
        </Menu>
      </Box>
    </Box>
  )
}

export default ProfileMenu
